import { useLingui } from '@lingui/react';
import { CheckCircle2, PenLine, Send, XCircle, type LucideIcon } from 'lucide-react';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import type { ShiftStatusType } from '@/types/shifts';
import { ShiftStatus } from '@/types/shifts';

interface ShiftStatusBadgeProps {
  /**
   * The wire types expose this as a free `string` (#1156), so the prop takes
   * one too; any value outside the known set renders as a neutral badge with
   * the raw value.
   */
  status: ShiftStatusType | (string & {});
  className?: string;
}

export const SHIFT_STATUS_TONES: Record<ShiftStatusType, { tone: StatusTone; icon?: LucideIcon }> = {
  [ShiftStatus.DRAFT]: { tone: 'neutral' },
  [ShiftStatus.PUBLISHED]: { tone: 'info', icon: Send },
  [ShiftStatus.CONFIRMED]: { tone: 'success', icon: CheckCircle2 },
  [ShiftStatus.CANCELLED]: { tone: 'danger', icon: XCircle },
};

/**
 * Fill, border and text of a shift block in the planner (week, day and month
 * views), in the same tones as the badge. Draft blocks stay dashed and muted.
 */
export const SHIFT_STATUS_BLOCK_CLASSES: Record<ShiftStatusType, string> = {
  [ShiftStatus.DRAFT]: 'bg-muted border-dashed border-border text-muted-foreground opacity-80',
  [ShiftStatus.PUBLISHED]: 'bg-info-muted border-solid border-info-on-surface/40 text-info-on-surface',
  [ShiftStatus.CONFIRMED]: 'bg-success-muted border-solid border-success-on-surface/40 text-success-on-surface',
  [ShiftStatus.CANCELLED]:
    'bg-destructive-muted border-solid border-destructive-on-surface/40 text-destructive-on-surface line-through',
};

export function ShiftStatusBadge({ status, className }: ShiftStatusBadgeProps) {
  const { i18n } = useLingui();
  const config = SHIFT_STATUS_TONES[status as ShiftStatusType] ?? { tone: 'neutral' as const };

  const labels: Partial<Record<ShiftStatusType | (string & {}), string>> = {
    [ShiftStatus.DRAFT]: i18n._({
      id: 'shifts.status.draft',
      message: 'Entwurf',
    }),
    [ShiftStatus.PUBLISHED]: i18n._({
      id: 'shifts.status.published',
      message: 'Veröffentlicht',
    }),
    [ShiftStatus.CONFIRMED]: i18n._({
      id: 'shifts.status.confirmed',
      message: 'Bestätigt',
    }),
    [ShiftStatus.CANCELLED]: i18n._({
      id: 'shifts.status.cancelled',
      message: 'Storniert',
    }),
  };

  return (
    <StatusBadge
      tone={config.tone}
      icon={config.icon}
      label={labels[status] ?? status}
      className={className}
    />
  );
}

/**
 * Second badge next to the status of a PUBLISHED or CONFIRMED shift that changed after
 * publication (#2038): the employee was pushed about the change and confirms again.
 */
export function ShiftChangedBadge({ className }: { className?: string }) {
  const { i18n } = useLingui();
  return (
    <StatusBadge
      tone="warning"
      icon={PenLine}
      label={i18n._({ id: 'shifts.lifecycle.changed', message: 'Changed since publication' })}
      className={className}
    />
  );
}
